// Pure helpers for LR *step* tags — values like "0-1000#yes;5000-7000#no",
// where each `startCm-endCm#value` section applies to one stretch of the
// feature's own geometry. No OpenLayers / orbis-lr-sdk imports so this is
// unit-testable with `node --test`; the decoding + geometry slicing adapter
// lives next door in `stepSections.js`.
//
// A step tag differs from a `:linear` one in that the value does NOT
// interpolate between offsets — it holds constant over each section and is
// simply absent outside them. "Absent" is meaningful (no restriction there),
// so a gap must render as the unrestricted style, not as the previous value.

// First section of a step chain: "<start>-<end>#" with an optionally omitted
// end ("1000-#no" = from 1000cm to the end of the geometry).
const STEP_CHAIN_START = /^\d+-\d*#/;

// A single open-ended section can also be written with no range at all —
// "27432#yes" means "from 274.32m to the end of the line". Real HDLR data does
// this: layer 88259 pairs `psv:step=0-27432#yes` with `bus:step=27432#yes`.
const OFFSET_CHAIN_START = /^\d+[-#]/;

/**
 * Does this tag value carry per-section offsets, as opposed to a plain scalar
 * ("yes") that applies to the whole feature?
 * @param {unknown} value
 * @returns {boolean}
 */
export function isStepEncodedValue(value) {
  return typeof value === "string" && STEP_CHAIN_START.test(value);
}

/**
 * Like isStepEncodedValue, but also accepts the bare-offset form above.
 *
 * Kept separate because that form is **ambiguous on value shape alone**:
 * `1234#yes` is equally the encoding of a `:spot` tag (at 1234cm) and of a
 * `:linear` chain's first point, and gss-wfs strips the `:step` / `:spot`
 * suffix when it decodes, so the key can't disambiguate either. Use this only
 * where the caller knows its tags are step-encoded enumerations — the two
 * lane-restriction styles, whose tags are yes/no. `isStepEncodedValue` stays
 * the conservative test for anything that runs across every layer's tags.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isOffsetSectionedValue(value) {
  return typeof value === "string" && OFFSET_CHAIN_START.test(value);
}

/**
 * Close every bare-offset section ("500#3", decoded with no end) at the start of
 * the element after it — what the SDK's getEndDistanceOfOffset does. Left open,
 * each offset would run to the end of the line, so in "0#2;500#3" the first
 * section would cover everything and win on every lookup. A trailing offset stays
 * open: it does run to the end. Explicit ranges are untouched.
 * @param {{startCm: number, endCm: number|null|undefined, value: string}[]} sections
 *   in chain order
 */
export function closeOffsetSections(sections) {
  return sections.map((section, i) => {
    const next = sections[i + 1];
    const open = section.endCm === null || section.endCm === undefined;
    return open && next && next.startCm > section.startCm
      ? { ...section, endCm: next.startCm }
      : section;
  });
}

/**
 * Clamp decoded sections onto a geometry of `lengthCm`, dropping the empty
 * ones. An `endCm` of null/undefined/Infinity means "to the end of the
 * geometry" — the LR spec allows the end to be omitted.
 * @param {{startCm: number, endCm: number|null|undefined, value: string}[]} sections
 * @param {number} lengthCm
 */
export function clampSections(sections, lengthCm) {
  if (!(lengthCm > 0)) return [];
  return sections
    .map((section) => ({
      startCm: Math.max(0, Math.min(section.startCm, lengthCm)),
      endCm:
        section.endCm === null ||
        section.endCm === undefined ||
        !Number.isFinite(section.endCm)
          ? lengthCm
          : Math.max(0, Math.min(section.endCm, lengthCm)),
      value: section.value,
    }))
    .filter((section) => section.endCm > section.startCm);
}

/**
 * The value covering `cm`, or undefined where no section covers it. Sections
 * are half-open [startCm, endCm) so touching sections don't both claim their
 * shared breakpoint.
 * @param {{startCm: number, endCm: number, value: string}[]} sections
 * @param {number} cm
 */
export function valueAtCm(sections, cm) {
  const hit = sections.find(
    (section) => cm >= section.startCm && cm < section.endCm,
  );
  return hit === undefined ? undefined : hit.value;
}

// Sub-centimetre slivers are invisible and would only multiply the styles OL
// has to render, so intervals shorter than this are dropped.
const MIN_SECTION_CM = 1;

/**
 * Cut [0, lengthCm] at every section boundary of every tag, then resolve each
 * resulting interval to one value per tag. The result is the smallest set of
 * stretches over which *all* the given tags are constant — i.e. exactly the
 * stretches that can be drawn as a single uniformly-styled piece of line.
 *
 * @param {Record<string, {startCm: number, endCm: number|null, value: string}[]>} sectionsByTag
 *   Decoded sections per tag name. Tags that aren't step-encoded are simply
 *   left out by the caller and stay whole-feature values.
 * @param {number} lengthCm
 * @returns {{startCm: number, endCm: number, values: Record<string, string|undefined>}[]}
 */
export function resolveSections(sectionsByTag, lengthCm) {
  const names = Object.keys(sectionsByTag);
  if (names.length === 0 || !(lengthCm > 0)) return [];

  const clamped = {};
  const breakpoints = new Set([0, lengthCm]);
  names.forEach((name) => {
    clamped[name] = clampSections(sectionsByTag[name], lengthCm);
    clamped[name].forEach((section) => {
      breakpoints.add(section.startCm);
      breakpoints.add(section.endCm);
    });
  });

  const ordered = Array.from(breakpoints).sort((a, b) => a - b);
  const intervals = [];
  for (let i = 1; i < ordered.length; i++) {
    const startCm = ordered[i - 1];
    const endCm = ordered[i];
    if (endCm - startCm < MIN_SECTION_CM) continue;
    // Sample at the midpoint: it is inside the interval whatever the
    // half-open/closed convention at the shared breakpoints.
    const midCm = (startCm + endCm) / 2;
    const values = {};
    names.forEach((name) => {
      values[name] = valueAtCm(clamped[name], midCm);
    });
    intervals.push({ startCm, endCm, values });
  }
  return intervals;
}

/**
 * Collapse neighbouring intervals whose styling key is identical, so a tag
 * that changes without changing the rendered colour doesn't fragment the line.
 * @template {{startCm: number, endCm: number}} T
 * @param {T[]} intervals
 * @param {(interval: T) => string} keyOf
 * @returns {(T & {key: string})[]}
 */
export function mergeAdjacent(intervals, keyOf) {
  const merged = [];
  intervals.forEach((interval) => {
    const key = keyOf(interval);
    const previous = merged[merged.length - 1];
    if (previous && previous.key === key) {
      previous.endCm = interval.endCm;
      return;
    }
    merged.push({ ...interval, key });
  });
  return merged;
}
